import { useState, useEffect } from 'react';
import { Event, EventType } from '../services/Types';
import { allEvents } from '../services/EventService';

// Global state management for events
let globalEventsState: Event[] = [];
let globalEventsErrorState: string | null = null;
let globalEventUpdatingState: boolean = false;
let globalEventSubscribers: Set<(events: Event[], error: string | null, updating: boolean) => void> = new Set();

// Function to notify all subscribers of state changes
const notifySubscribers = () => {
  globalEventSubscribers.forEach(callback => callback(globalEventsState, globalEventsErrorState, globalEventUpdatingState));
};

// Function to update global state
const updateGlobalState = (events: Event[], error: string | null, updating: boolean) => {
  globalEventsState = events;
  globalEventsErrorState = error;
  globalEventUpdatingState = updating;
  notifySubscribers();
};

export const useAllEvents = () => {
  const [events, setEvents] = useState<Event[]>(globalEventsState);
  const [errorUseAllEvents, setErrorUseAllEvents] = useState<string | null>(globalEventsErrorState);
  const [updating, setUpdating] = useState<boolean>(globalEventUpdatingState);

  useEffect(() => {
    // Subscribe to global state changes
    const handleStateChange = (newEvents: Event[], newError: string | null, newUpdating: boolean) => {
      setEvents(newEvents);
      setErrorUseAllEvents(newError);
      setUpdating(newUpdating);
    };

    globalEventSubscribers.add(handleStateChange);

    // Load events if not already loaded
    if (globalEventsState.length === 0) {
      getAllEvents();
    }

    // Cleanup subscription on unmount
    return () => {
      globalEventSubscribers.delete(handleStateChange);
    };
  }, []);

  // TODO: The cache should be for each event so we can reset a single event
  const resetAllEventsCache = () => {
    updateGlobalState([], null, false);
    setTimeout(() => {
      getAllEvents();
    }, 500);
  }

  // Fetch all events
  const getAllEvents = async () => {
    if (globalEventsState.length > 0) {
      return true;
    }
    if (globalEventUpdatingState) {
      return true;
    }
    try {
      updateGlobalState(globalEventsState, globalEventsErrorState, true);
      const { data } = await allEvents();
      if (data?.events?.data && data.events.statusCode === 200) {
        updateGlobalState(data.events.data, null, false);
        return true;
      } else {
        updateGlobalState([], 'Error fetching events. Please try again later.', false);
        return false;
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      updateGlobalState([], 'Error fetching events. Please try again later.', false);
      return false;
    }
  };

  const getSeriesParent = (seriesId: number) => {
    const seriesEvents = globalEventsState.filter(event => event.seriesId === seriesId)
    if (seriesEvents.length > 0) {
      const seriesParent = seriesEvents.find(event => event.eventType === EventType.SERIES)
      return seriesParent ? seriesParent : null;
    }
    return null;
  };

  const getSeriesTitle = (seriesId: number) => {
    const seriesParent = getSeriesParent(seriesId)
    return seriesParent ? seriesParent.title : 'TBD';
  }

  return {
    events,
    errorUseAllEvents,
    updating,
    getAllEvents,
    resetAllEventsCache,
    getSeriesParent,
    getSeriesTitle
  };
}; 